import { External } from '../../external';

export const CIRCLE_LOCALIZED_FIELDS = ['name', 'description', 'settings.requestAccess.title', 'settings.requestAccess.description'];
export const PAGE_LOCALIZED_FIELDS = ['name', 'description', 'settings.badgeLabel'];
export const GROUP_LOCALIZED_FIELDS = ['name', 'description', 'settings.badgeLabel'];

const BASE_KEY = '__localeBaseValues';

function getCookieValue(name) {
  if (typeof document === 'undefined' || !document.cookie)
    return '';
  const parts = document.cookie.split(';');
  for (let i = 0; i < parts.length; i++) {
    const item = (parts[i] || '').trim();
    if (item.indexOf(name + '=') === 0)
      return decodeURIComponent(item.substring(name.length + 1));
  }
  return '';
}

function getByPath(source, path) {
  if (!source || !path)
    return undefined;
  const parts = path.split('.');
  let curr = source;
  for (let i = 0; i < parts.length; i++) {
    if (curr == null)
      return undefined;
    curr = curr[parts[i]];
  }
  return curr;
}

function setByPath(target, path, value) {
  if (!target || !path)
    return;
  const parts = path.split('.');
  let curr = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    if (!curr[key] || typeof curr[key] !== 'object')
      curr[key] = {};
    curr = curr[key];
  }
  curr[parts[parts.length - 1]] = value;
}

function valuesEqual(first, second) {
  return JSON.stringify(first) === JSON.stringify(second);
}

function getBaseValues(entity, fields) {
  if (!entity || !fields || !fields.length)
    return {};
  let baseValues = entity[BASE_KEY];
  if (!baseValues) {
    baseValues = {};
    fields.forEach((field) => {
      baseValues[field] = getByPath(entity, field);
    });
    Object.defineProperty(entity, BASE_KEY, {
      value: baseValues,
      enumerable: false,
      configurable: true,
      writable: true
    });
  }
  return baseValues;
}

export function getLmsLocaleContext() {
  let settings = External.WebsiteSettings && External.WebsiteSettings.getSettings ? (External.WebsiteSettings.getSettings() || {}) : {};
  let defaultLocale = (settings.locale || (External.Website && External.Website.defaultEditLocale) || 'en').toString();
  let multilingualOn = settings.multilingualEnabled === true || settings.multilingualEnabled === 1 || settings.multilingualEnabled === '1';
  let enabledLocales = [defaultLocale];
  if (multilingualOn && Array.isArray(settings.websiteLocales)) {
    settings.websiteLocales.forEach((item) => {
      const locale = (item == null ? '' : item).toString();
      if (locale && enabledLocales.indexOf(locale) < 0)
        enabledLocales.push(locale);
    });
  }
  let currentLocale = '';
  if (typeof window !== 'undefined' && window.location && window.location.search) {
    const match = window.location.search.match(/[?&]locale=([^&#]+)/);
    if (match && match[1])
      currentLocale = decodeURIComponent(match[1]);
  }
  if (!currentLocale)
    currentLocale = getCookieValue('current-locale');
  if (!currentLocale && External.Website)
    currentLocale = External.Website.currentEditLocale || External.Website.defaultEditLocale || defaultLocale;
  if (!currentLocale)
    currentLocale = defaultLocale;
  currentLocale = (currentLocale || '').toString();
  // Only locales enabled for THIS site may divert edits into a locale bucket. The
  // current-locale cookie is domain-wide (shared across sites on a WL admin domain) and
  // lives for years, so an unvalidated value here silently buckets edits under a locale
  // the site doesn't have — which reads as "my changes were not saved".
  if (enabledLocales.indexOf(currentLocale) < 0)
    currentLocale = defaultLocale;
  return {
    currentLocale,
    defaultLocale
  };
}

// Locale overrides are stored inside the entity's `settings` JSON blob (settings.locales)
// because the external LMS persists `settings` wholesale but drops unknown top-level fields
// (a top-level `locales` does NOT survive a save round-trip). The bucket is keyed by locale
// and each bucket maps entity-relative field paths to their localized value.
const LOCALE_STORE_PATH = 'settings.locales';

function getLocaleStore(entity) {
  const store = getByPath(entity, LOCALE_STORE_PATH);
  return store && typeof store === 'object' ? store : null;
}

export function applyLocalizedFields(entity, fields, localeContext = getLmsLocaleContext()) {
  if (!entity || !fields || !fields.length)
    return entity;
  const baseValues = getBaseValues(entity, fields);
  const locale = localeContext.currentLocale;
  const defaultLocale = localeContext.defaultLocale;
  const store = getLocaleStore(entity);
  const localeBucket = store ? store[locale] : null;

  fields.forEach((field) => {
    const value = locale && locale !== defaultLocale && localeBucket && Object.prototype.hasOwnProperty.call(localeBucket, field)
      ? localeBucket[field]
      : baseValues[field];
    setByPath(entity, field, value);
  });
  return entity;
}

export function applyLocalizedCircleDetails(circleDetails, localeContext = getLmsLocaleContext()) {
  if (!circleDetails)
    return circleDetails;
  applyLocalizedFields(circleDetails.circle, CIRCLE_LOCALIZED_FIELDS, localeContext);
  (circleDetails.pages || []).forEach((page) => applyLocalizedFields(page, PAGE_LOCALIZED_FIELDS, localeContext));
  (circleDetails.groups || []).forEach((group) => {
    applyLocalizedFields(group.pageGroup, GROUP_LOCALIZED_FIELDS, localeContext);
    (group.pages || []).forEach((page) => applyLocalizedFields(page, PAGE_LOCALIZED_FIELDS, localeContext));
  });
  return circleDetails;
}

export function buildLocalizedPayload(entity, fields, localeContext = getLmsLocaleContext()) {
  const payload = JSON.parse(JSON.stringify(entity || {}));
  if (!entity || !fields || !fields.length)
    return payload;

  const locale = localeContext.currentLocale;
  const defaultLocale = localeContext.defaultLocale;
  const baseValues = getBaseValues(entity, fields);

  if (!locale || locale === defaultLocale) {
    fields.forEach((field) => {
      baseValues[field] = getByPath(entity, field);
    });
    return payload;
  }

  fields.forEach((field) => {
    setByPath(payload, field, baseValues[field]);
  });

  const existingStore = getByPath(payload, LOCALE_STORE_PATH);
  const locales = existingStore && typeof existingStore === 'object' ? existingStore : {};
  const localeBucket = locales[locale] && typeof locales[locale] === 'object' ? locales[locale] : {};

  fields.forEach((field) => {
    const localizedValue = getByPath(entity, field);
    if (valuesEqual(localizedValue, baseValues[field]))
      delete localeBucket[field];
    else
      localeBucket[field] = localizedValue;
  });

  if (Object.keys(localeBucket).length)
    locales[locale] = localeBucket;
  else
    delete locales[locale];

  if (Object.keys(locales).length)
    setByPath(payload, LOCALE_STORE_PATH, locales);
  else if (payload.settings)
    delete payload.settings.locales;

  // A stale top-level locales bucket from earlier builds would shadow the settings one
  // on the next load, so drop it from the saved payload.
  if (payload.locales)
    delete payload.locales;

  return payload;
}
